import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CustomerCateringView = ({ userId }) => {
  const [cateringBookings, setCateringBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCateringBookings();
  }, [userId]);

  const loadCateringBookings = async () => {
    try {
      const response = await axios.get(`/catering/bookings/customer/${userId}`);
      setCateringBookings(response.data.bookings || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load catering bookings:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading catering orders...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">🍽️ My Catering Orders</h2>
        
        {/* Call to Action */}
        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6 mb-6">
          <h3 className="font-bold text-blue-900 text-lg mb-2">Need Catering for 10-100 Guests?</h3>
          <p className="text-blue-800 mb-3">
            We offer professional catering services with three tiers:
          </p>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-white rounded p-3">
              <p className="font-bold text-gray-900">Basic</p>
              <p className="text-sm text-gray-600">$75/plate</p>
              <p className="text-xs text-gray-500">3 courses</p>
            </div>
            <div className="bg-white rounded p-3">
              <p className="font-bold text-gray-900">Premium</p>
              <p className="text-sm text-gray-600">$100/plate</p>
              <p className="text-xs text-gray-500">4 courses</p>
            </div>
            <div className="bg-white rounded p-3">
              <p className="font-bold text-gray-900">Deluxe</p>
              <p className="text-sm text-gray-600">$150/plate</p>
              <p className="text-xs text-gray-500">5 courses</p>
            </div>
          </div>
          <div className="bg-blue-100 rounded-lg p-4">
            <p className="font-bold text-blue-900 text-lg mb-1">📞 Call us to book:</p>
            <p className="text-3xl font-bold text-blue-900">614-517-6320</p>
          </div>
        </div>

        {/* Bookings List */}
        {cateringBookings.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🍽️</div>
            <p className="text-gray-600 text-lg">No catering orders yet</p>
            <p className="text-gray-500 text-sm mt-2">Call 614-517-6320 to place your first catering order</p>
          </div>
        ) : (
          <div className="space-y-4">
            {cateringBookings.map(booking => (
              <div key={booking.id} className="border-2 border-gray-200 rounded-lg p-6 hover:border-orange-300 transition">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="font-bold text-xl text-gray-900">{booking.event_type.charAt(0).toUpperCase() + booking.event_type.slice(1)} Event</h4>
                    <p className="text-gray-600">Chef: {booking.chef_name}</p>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                    booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {booking.status.toUpperCase()}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Date & Time</p>
                    <p className="text-gray-900">
                      {new Date(booking.date).toLocaleDateString()} at {new Date(booking.date).toLocaleTimeString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Guests</p>
                    <p className="text-gray-900">{booking.guest_count} people</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Tier</p>
                    <p className="text-gray-900 capitalize">{booking.tier} (${booking.price_per_plate}/plate)</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Location</p>
                    <p className="text-gray-900">{booking.service_city}, {booking.service_state}</p>
                  </div>
                </div>

                {booking.special_requests && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 font-medium">Special Requests</p>
                    <p className="text-gray-900">{booking.special_requests}</p>
                  </div>
                )}

                {booking.dietary_restrictions && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 font-medium">Dietary Restrictions</p>
                    <p className="text-gray-900">{booking.dietary_restrictions}</p>
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-700">Total Amount</span>
                    <span className="text-2xl font-bold text-orange-600">${booking.total}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Includes {booking.guest_count} plates, setup ($300), delivery ($125), and fees
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerCateringView;
